import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, ArrowRight, Star, ArrowUpRight } from "lucide-react";
import { FORM_SERVICES, CONTACT } from "@/data/site";
import { Reveal, SectionHead } from "./Reveal";
import { ContactCard } from "./ContactCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", plz_city: "", services: [], preferred_date: "", message: "", consent: false };
const inputCls = "mt-2 w-full rounded-xl border border-[#111511]/10 bg-[#FAFAF7] px-4 py-3.5 text-base text-[#111511] placeholder:text-[#8A9088] focus:outline-none focus:border-[#2F5D3A] focus:bg-white focus:ring-4 focus:ring-[#9CC26A]/25 transition-[border-color,background-color,box-shadow]";

const Field = ({ label, id, required, ...props }) => (
  <label className="block">
    <span className="text-sm font-medium text-[#30352F]">{label}{required && <span className="text-[#5E8C3A]"> *</span>}</span>
    <input id={id} required={required} data-testid={`inquiry-input-${id}`} className={inputCls} {...props} />
  </label>
);

const ReviewHint = () => (
  <div data-testid="review-hint" className="mt-10 rounded-2xl bg-[#FAFAF7] border border-[#111511]/[0.08] p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
    <span className="flex gap-0.5 shrink-0">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-4 h-4 fill-[#E8B23A] text-[#E8B23A]" />)}</span>
    <p className="flex-1 text-[15px] text-[#30352F] leading-relaxed"><strong className="font-semibold text-[#111511]">Schon zufrieden mit uns?</strong> Über eine kurze Bewertung auf Google freuen wir uns sehr – sie hilft anderen bei der Entscheidung.</p>
    <a href={CONTACT.googleReviewUrl} target="_blank" rel="noreferrer" data-testid="review-hint-button"
      className="group shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-[#111511] text-white px-5 py-3 text-sm font-medium transition-colors duration-300 hover:bg-[#2F5D3A]">
      Auf Google bewerten <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
    </a>
  </div>
);

const Success = ({ onReset }) => (
  <div data-testid="inquiry-success-toast" className="h-full rounded-[28px] border border-[#111511]/[0.08] bg-white p-10 lg:p-14 flex flex-col justify-center">
    <span className="w-14 h-14 rounded-full bg-[#9CC26A] text-[#111511] flex items-center justify-center"><Check className="w-6 h-6" /></span>
    <h3 className="mt-8 font-head text-4xl font-semibold tracking-tight">Vielen Dank!</h3>
    <p className="mt-4 text-lg text-[#5B615A] max-w-lg">Ihre Anfrage ist eingegangen. Wir melden uns zeitnah bei Ihnen, um alles Weitere zu besprechen.</p>
    <button data-testid="inquiry-new-button" onClick={onReset} className="mt-8 self-start text-[#2F5D3A] font-medium underline underline-offset-4">Weitere Anfrage senden</button>
    {CONTACT.googleReviewUrl && <ReviewHint />}
  </div>
);

export const Inquiry = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (s) => setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));

  useEffect(() => {
    const onPrefill = (e) => {
      const services = Array.isArray(e?.detail?.services) ? e.detail.services : [];
      setDone(false);
      setForm((f) => ({ ...f, services, message: f.message || `Pflege im ${e?.detail?.month ?? ""}` }));
      toast.success("Leistungen wurden im Formular ausgewählt.");
    };
    window.addEventListener("prefill-inquiry", onPrefill);
    return () => window.removeEventListener("prefill-inquiry", onPrefill);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Bitte stimmen Sie der Datenverarbeitung zu."); return; }
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setDone(true);
      setForm(EMPTY);
      toast.success("Danke! Ihre Anfrage ist eingegangen.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Bitte prüfen Sie Ihre Angaben (z. B. E-Mail-Adresse).");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="anfrage" data-testid="inquiry-section" className="py-24 lg:py-36 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <SectionHead eyebrow="Kontakt & Anfrage" lines={["Jetzt Anfrage senden.", <span className="text-[#5E8C3A]">Kostenlos & unverbindlich.</span>]}
          text="Erzählen Sie uns kurz, was in Ihrem Garten zu tun ist – wir melden uns mit einem passenden Angebot." />
        <div className="mt-14 lg:mt-16 grid lg:grid-cols-12 gap-5">
          <Reveal className="lg:col-span-5 min-w-0"><ContactCard /></Reveal>
          <Reveal className="lg:col-span-7 min-w-0" delay={0.1}>
            {done ? <Success onReset={() => setDone(false)} /> : (
              <form data-testid="inquiry-form-container" onSubmit={submit} className="rounded-[28px] border border-[#111511]/[0.08] bg-white p-6 sm:p-10 shadow-[0_30px_60px_-40px_rgba(17,21,17,0.25)]">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Name" id="name" required autoComplete="name" value={form.name} onChange={set("name")} placeholder="Vor- und Nachname" />
                  <Field label="Telefon" id="phone" type="tel" required autoComplete="tel" value={form.phone} onChange={set("phone")} placeholder="Für Rückfragen" />
                  <Field label="E-Mail" id="email" type="email" required autoComplete="email" value={form.email} onChange={set("email")} placeholder="name@beispiel.de" />
                  <Field label="Ort" id="plz-city" required value={form.plz_city} onChange={set("plz_city")} placeholder="z. B. 46236 Bottrop" />
                </div>
                <fieldset className="mt-7">
                  <legend className="text-sm font-medium text-[#30352F]">Gewünschte Gartenarbeit</legend>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {FORM_SERVICES.map((s, i) => {
                      const on = form.services.includes(s);
                      return (
                        <button type="button" key={s} onClick={() => toggle(s)} data-testid={`inquiry-service-${i}`} aria-pressed={on}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-300 ${on ? "bg-[#111511] border-[#111511] text-white" : "border-[#111511]/10 bg-[#FAFAF7] text-[#30352F] hover:border-[#111511]/30"}`}>
                          {on && <Check className="w-3.5 h-3.5" />} {s}
                        </button>
                      );
                    })}
                  </div>
                </fieldset>
                <div className="mt-7 grid sm:grid-cols-3 gap-5">
                  <Field label="Wunschtermin" id="date" type="date" value={form.preferred_date} onChange={set("preferred_date")} />
                  <label className="block sm:col-span-2">
                    <span className="text-sm font-medium text-[#30352F]">Nachricht</span>
                    <textarea data-testid="inquiry-input-message" rows={3} value={form.message} onChange={set("message")} placeholder="z. B. Gartengröße, gewünschter Rhythmus …" className={`${inputCls} resize-none`} />
                  </label>
                </div>
                <label className="mt-6 flex items-start gap-3 text-sm text-[#5B615A] cursor-pointer">
                  <input type="checkbox" data-testid="inquiry-consent-checkbox" checked={form.consent} onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))} className="mt-0.5 w-4 h-4 accent-[#2F5D3A]" />
                  <span>Ich bin einverstanden, dass meine Angaben zur Bearbeitung der Anfrage gespeichert werden. Mehr in der <Link to="/datenschutz" className="underline underline-offset-2 text-[#2F5D3A]">Datenschutzerklärung</Link>.</span>
                </label>
                <button type="submit" disabled={sending} data-testid="inquiry-submit-button"
                  className="group mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#111511] text-white px-8 py-4 text-[15px] font-medium transition-colors duration-300 hover:bg-[#2F5D3A] disabled:opacity-60">
                  {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                  Kostenloses Angebot anfragen
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};
